// Escalera de recarga: la parte que garantiza que la version nueva realmente
// entre, no solo que la detectemos.
//
// El problema real es que `location.reload()` NO es una garantia. Segun el
// browser y como se llego a la pagina (restauracion de sesion, bfcache, cache
// de disco con heuristicas propias, un proxy corporativo), un reload puede
// volver a servir exactamente el mismo HTML viejo. Por eso cada intento es
// mas agresivo que el anterior, y despues de cada uno verificamos si funciono
// comparando el buildId con el que teniamos.
//
//   0. reload() a secas.                        — el 99% de los casos.
//   1. Purga de Cache Storage + baja cualquier  — mata un service worker
//      service worker, despues reload.            zombi de una version previa.
//   2. Navegacion a la misma URL con un         — cache-buster en la URL:
//      parametro __v=<buildId>.                   entrada de cache distinta,
//                                                 imposible de satisfacer con
//                                                 la respuesta vieja.
//   3. Se agoto: mostramos un cartel bloqueante con instrucciones.
//
// El estado vive en sessionStorage: sobrevive a la recarga (que es el punto)
// y muere con la pestaña. Si viviera en localStorage, una pestaña trabada
// contaminaria a las demas.

import { readJson, remove, writeJson } from './storage';
import type { UpdateOutcome } from './types';

const KEY = 'authsys:version-guard:reload';
const BUSTER_PARAM = '__v';
export const MAX_ATTEMPTS = 3;

interface ReloadState {
  /** buildId que teniamos cuando decidimos recargar. */
  from: string;
  /** buildId que esperamos ver despues de recargar. */
  target: string;
  /** Escalon que se ejecuto (0-based). */
  attempt: number;
  at: number;
}

export type ReloadVerdict =
  /** No veniamos de una recarga del guard. */
  | { kind: 'none' }
  /** Funciono: estamos en una version distinta a la que teniamos. */
  | { kind: 'updated'; outcome: UpdateOutcome }
  /** El browser nos devolvio lo mismo. Hay que escalar. */
  | { kind: 'failed'; nextAttempt: number; target: string; from: string }
  /** Se agotaron los escalones. Intervencion manual. */
  | { kind: 'exhausted'; target: string; from: string };

/**
 * Se llama una sola vez al arrancar, antes de cualquier chequeo. Decide si
 * venimos de una recarga nuestra y si funciono.
 */
export function resolvePendingReload(currentBuildId: string): ReloadVerdict {
  const state = readJson<ReloadState>('session', KEY);
  if (!state || typeof state.from !== 'string' || typeof state.target !== 'string') {
    return { kind: 'none' };
  }

  // Sin un buildId comparable no hay forma de saber si la recarga funciono, y
  // escalar a ciegas terminaria en el cartel de "no pudimos actualizar" por
  // nada. Pasa en el dev server (buildId fijo 'dev') y con cualquier HTML que
  // no haya pasado por el plugin de sellado.
  if (currentBuildId === 'dev' || currentBuildId === 'unknown') {
    remove('session', KEY);
    return { kind: 'none' };
  }

  // Exito = cambiamos de build. Comparamos contra `from` y no contra `target`
  // a proposito: si mientras recargabamos se deployo una version todavia mas
  // nueva, llegamos a una tercera y eso tambien es exito.
  if (currentBuildId !== state.from && currentBuildId !== 'unknown') {
    remove('session', KEY);
    cleanBusterFromUrl();
    return {
      kind: 'updated',
      outcome: {
        from: state.from,
        to: currentBuildId,
        at: Date.now(),
        attempts: state.attempt,
      },
    };
  }

  const nextAttempt = state.attempt + 1;
  if (nextAttempt >= MAX_ATTEMPTS) {
    remove('session', KEY);
    return { kind: 'exhausted', target: state.target, from: state.from };
  }
  return { kind: 'failed', nextAttempt, target: state.target, from: state.from };
}

/** Saca el ?__v= de la barra de direcciones una vez que cumplio su funcion. */
function cleanBusterFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(BUSTER_PARAM)) return;
    url.searchParams.delete(BUSTER_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // URL rara — no vale la pena romper nada por limpiar la barra
  }
}

async function purgeCaches(): Promise<void> {
  // Cache Storage: no lo usamos, pero si alguna vez hubo un service worker
  // (o lo agrega alguien mañana) sus entradas ganan sobre la red.
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // sin permisos / no soportado
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // idem
  }
}

/**
 * Ejecuta el escalon `attempt`. Persiste el estado ANTES de navegar — si no,
 * la recarga se lleva puesta la evidencia de que fue nuestra.
 */
export async function executeReload(params: {
  from: string;
  target: string;
  attempt: number;
}): Promise<void> {
  const { from, target, attempt } = params;
  writeJson('session', KEY, { from, target, attempt, at: Date.now() } satisfies ReloadState);

  if (attempt >= 1) {
    await purgeCaches();
  }

  if (attempt >= 2) {
    // Cache-buster en la URL. Cambia la clave de cache HTTP del documento, asi
    // que ni el disk cache ni un proxy pueden satisfacerla con lo viejo.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set(BUSTER_PARAM, target);
      window.location.replace(url.toString());
      return;
    } catch {
      // cae al reload de abajo
    }
  }

  window.location.reload();
}

/** Aborta una recarga en curso (ej: el usuario cancelo el countdown). */
export function clearPendingReload(): void {
  remove('session', KEY);
}
